import { create } from 'zustand';

/** One WP slice prepare/review signoff (userId lists + display + dates). */
export type WpSignoffState = {
  preparedUserIds: number[];
  reviewedUserIds: number[];
  preparedBy: string | null;
  reviewedBy: string | null;
  preparedDate: string | null;
  reviewedDate: string | null;
};

export type WpSignoffKeyParts = {
  companyId: number;
  annualId: number;
  code: string;
  yearOfAssessment?: string | null;
};

const emptySignoff = (): WpSignoffState => ({
  preparedUserIds: [],
  reviewedUserIds: [],
  preparedBy: null,
  reviewedBy: null,
  preparedDate: null,
  reviewedDate: null,
});

function buildKey(parts: WpSignoffKeyParts): string {
  const code = String(parts.code || '').trim().toUpperCase();
  const yoa = String(parts.yearOfAssessment || '').trim();
  return `${parts.companyId}:${parts.annualId}:${code}:${yoa}`;
}

type Store = {
  byKey: Record<string, WpSignoffState>;
  /** Locked prepared ids for current editor session (historical + current user). */
  lockedPreparedByKey: Record<string, number[]>;
  hydrate: (parts: WpSignoffKeyParts, patch: Partial<WpSignoffState>, lockPrepared?: number[]) => void;
  get: (parts: WpSignoffKeyParts) => WpSignoffState;
  getLockedPrepared: (parts: WpSignoffKeyParts) => number[];
  setPreparedUserIds: (parts: WpSignoffKeyParts, ids: number[]) => void;
  setReviewedUserIds: (parts: WpSignoffKeyParts, ids: number[]) => void;
  setPreparedDate: (parts: WpSignoffKeyParts, date: string | null) => void;
  setReviewedDate: (parts: WpSignoffKeyParts, date: string | null) => void;
  /** Payload fragment to merge into WP save details. */
  toSavePatch: (parts: WpSignoffKeyParts) => Record<string, unknown>;
};

function toNumIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  for (const item of raw) {
    const n = Number(item);
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
}

export const useWpSignoffStore = create<Store>((set, get) => ({
  byKey: {},
  lockedPreparedByKey: {},

  hydrate: (parts, patch, lockPrepared) => {
    const key = buildKey(parts);
    const prev = get().byKey[key] || emptySignoff();
    const next: WpSignoffState = {
      ...prev,
      ...patch,
      preparedUserIds: patch.preparedUserIds != null
        ? toNumIds(patch.preparedUserIds)
        : prev.preparedUserIds,
      reviewedUserIds: patch.reviewedUserIds != null
        ? toNumIds(patch.reviewedUserIds)
        : prev.reviewedUserIds,
    };
    const locked = lockPrepared != null
      ? toNumIds(lockPrepared)
      : (get().lockedPreparedByKey[key] || toNumIds(next.preparedUserIds));
    set((s) => ({
      byKey: { ...s.byKey, [key]: next },
      lockedPreparedByKey: { ...s.lockedPreparedByKey, [key]: locked },
    }));
  },

  get: (parts) => get().byKey[buildKey(parts)] || emptySignoff(),

  getLockedPrepared: (parts) => get().lockedPreparedByKey[buildKey(parts)] || [],

  setPreparedUserIds: (parts, ids) => {
    const key = buildKey(parts);
    const locked = new Set(get().lockedPreparedByKey[key] || []);
    const nextIds = toNumIds(ids);
    for (const id of locked) {
      if (!nextIds.includes(id)) nextIds.push(id);
    }
    set((s) => ({
      byKey: {
        ...s.byKey,
        [key]: { ...(s.byKey[key] || emptySignoff()), preparedUserIds: nextIds },
      },
    }));
  },

  setReviewedUserIds: (parts, ids) => {
    const key = buildKey(parts);
    set((s) => ({
      byKey: {
        ...s.byKey,
        [key]: { ...(s.byKey[key] || emptySignoff()), reviewedUserIds: toNumIds(ids) },
      },
    }));
  },

  setPreparedDate: (parts, date) => {
    const key = buildKey(parts);
    set((s) => ({
      byKey: {
        ...s.byKey,
        [key]: { ...(s.byKey[key] || emptySignoff()), preparedDate: date },
      },
    }));
  },

  setReviewedDate: (parts, date) => {
    const key = buildKey(parts);
    set((s) => ({
      byKey: {
        ...s.byKey,
        [key]: { ...(s.byKey[key] || emptySignoff()), reviewedDate: date },
      },
    }));
  },

  toSavePatch: (parts) => {
    const state = get().get(parts);
    return {
      preparedUserIds: state.preparedUserIds,
      reviewedUserIds: state.reviewedUserIds,
      preparedDate: state.preparedDate,
      reviewedDate: state.reviewedDate,
    };
  },
}));

/** Hydrate store from GET / save merge payload fields. */
export function hydrateWpSignoffFromPayload(
  parts: WpSignoffKeyParts,
  payload: Record<string, unknown> | null | undefined,
): void {
  if (!payload || typeof payload !== 'object') return;
  const preparedUserIds = toNumIds(payload.preparedUserIds);
  const reviewedUserIds = toNumIds(payload.reviewedUserIds);
  const hasAny =
    preparedUserIds.length > 0
    || reviewedUserIds.length > 0
    || payload.preparedBy != null
    || payload.reviewedBy != null
    || payload.preparedDate != null
    || payload.reviewedDate != null;
  if (!hasAny) return;
  useWpSignoffStore.getState().hydrate(
    parts,
    {
      preparedUserIds,
      reviewedUserIds,
      preparedBy: payload.preparedBy != null ? String(payload.preparedBy) : null,
      reviewedBy: payload.reviewedBy != null ? String(payload.reviewedBy) : null,
      preparedDate: payload.preparedDate != null ? String(payload.preparedDate) : null,
      reviewedDate: payload.reviewedDate != null ? String(payload.reviewedDate) : null,
    },
    preparedUserIds,
  );
}
